import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { SupportedCurrency } from '../../constants';
import { localeService } from '../../helpers';
import { selectPlans } from '../../../../store/features/system';
export function checkCurrency(currency) {
    let userCurrency = currency ? currency.toUpperCase() : '';
    if (!Object.values(SupportedCurrency).includes(userCurrency)) {
        userCurrency = SupportedCurrency.usd;
    }
    return userCurrency;
}
export function getPlanName(item, currency) {
    const userCurrency = checkCurrency(currency || SupportedCurrency.usd);
    return `plan-default-${userCurrency.toLowerCase()}-${item}`;
}
export function getHighestPlanVersion(planName, currency, plans) {
    const fullPlanName = getPlanName(planName, currency);
    const removeVersion = (item) => {
        return item.slice(0, Math.max(0, fullPlanName.length - 2));
    };
    const getVersion = (item) => {
        return Number.parseInt(item.slice(Math.max(0, item.lastIndexOf('-v') + 2)));
    };
    const filteredPlans = plans.filter((plan) => [removeVersion(plan.id), removeVersion(plan.stripePlan)].includes(removeVersion(fullPlanName)));
    const highestVersion = (a, b) => {
        const aVersion = Math.max(getVersion(a.id), getVersion(a.stripePlan));
        const bVersion = Math.max(getVersion(b.id), getVersion(b.stripePlan));
        return bVersion - aVersion;
    };
    const sortedPlans = filteredPlans.sort(highestVersion);
    return sortedPlans[0];
}
export function getPriceValue({ item, currency, plans }) {
    const userCurrency = checkCurrency(currency || SupportedCurrency.usd);
    const stripePlans = Array.isArray(plans) ? plans : [];
    if (typeof item === 'string') {
        if (stripePlans.length === 0) {
            return null;
        }
        const planName = getPlanName(item, userCurrency);
        const plan = stripePlans.find((stripePlan) => stripePlan.id === planName || stripePlan.stripePlan === planName);
        if (!plan) {
            return null;
        }
        return plan.pricePerSeat / 100;
    }
    return item;
}
export function getPrice({ item, currency, plans, showDecimals = false, }) {
    const userCurrency = checkCurrency(currency);
    let planObj = null;
    const stripePlans = Array.isArray(plans) ? plans : [];
    if (typeof item === 'string') {
        if (stripePlans.length === 0) {
            return '';
        }
        const planName = getPlanName(item, userCurrency);
        const plan = stripePlans.find((stripePlan) => stripePlan.id === planName || stripePlan.stripePlan === planName);
        if (!plan) {
            return '';
        }
        planObj = { ...plan, pricePerSeat: plan.pricePerSeat / 100 };
    }
    else {
        planObj = { pricePerSeat: item };
    }
    return localeService.instance.formatNumber(planObj.pricePerSeat, {
        style: 'currency',
        currency: userCurrency,
        minimumFractionDigits: showDecimals ? 2 : 0,
        maximumFractionDigits: showDecimals ? 2 : 0,
    });
}
export function Price({ item, currency, showDecimals = false }) {
    const plans = useSelector(selectPlans);
    const userCurrency = useMemo(() => checkCurrency(currency), [currency]);
    const planName = useMemo(() => {
        return getPlanName(item, userCurrency);
    }, [item, userCurrency]);
    const getPlan = useCallback(() => {
        if (typeof item === 'string') {
            const plan = plans.find((stripePlan) => stripePlan.id === planName || stripePlan.stripePlan === planName);
            if (!plan) {
                return null;
            }
            return { ...plan, pricePerSeat: plan.pricePerSeat / 100 };
        }
        else {
            return { pricePerSeat: item };
        }
    }, [item, planName, plans]);
    const plan = useMemo(() => {
        return getPlan();
    }, [getPlan]);
    if (!Array.isArray(plans) || plans.length === 0) {
        console.warn('No stripe plans found');
        return null;
    }
    if (plan == null) {
        console.warn('No matching plan found');
        return null;
    }
    return (<>
      {localeService.instance.formatNumber(plan.pricePerSeat, {
            style: 'currency',
            currency: userCurrency,
            minimumFractionDigits: showDecimals ? 2 : 0,
            maximumFractionDigits: showDecimals ? 2 : 0,
        })}
    </>);
}
